import React from "react";
import { useTranslation } from "react-i18next";
import { translateOptions } from "../../helpers/optionLabels";
import { useTheme } from "@mui/material";
import Typography from "components/basic/typography";
import ElevatorImage from "resources/images/ElevatorImage.png";
import { useNavigate } from "react-router-dom";
import AutoComplete from "components/basic/autoComplete";
import { useSnack } from "hooks/useSnack";
import { ChargerEstimatorToolsPagesProps } from "pages/chargerEstimator";
import FormPageLayout from "components/basic/formPageLayout";

const Preferences: React.FC<ChargerEstimatorToolsPagesProps> = ({
  chargerEstimatorData,
  setChargerEstimatorData,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("estimator");
  const navigate = useNavigate();
  const showSnack = useSnack();

  const purchaseOptions = [
    { id: 1, label: "purchase charger" },
    { id: 2, label: "lease-to-own charger" },
    { id: 3, label: "provide own charger" },
  ];
  const yesNoOptions = [
    { id: 1, label: "Yes" },
    { id: 2, label: "No" },
  ];

  const purchaseOrLease = chargerEstimatorData.purchaseType
    ? purchaseOptions.find(
        (opt) => opt.label === chargerEstimatorData.purchaseType,
      ) || null
    : null;

  const toYesNoId = (value: boolean | undefined) =>
    value === undefined ? null : value ? 1 : 2;

  const hasElevator = toYesNoId(chargerEstimatorData.elevator);
  const hasPool = toYesNoId(chargerEstimatorData.pool);
  const hasHotTub = toYesNoId(chargerEstimatorData.hotTub);

  const handleNext = () => {
    if (!chargerEstimatorData.purchaseType) {
      showSnack(t("preferences.purchase_required"), "error");
      return;
    }
    if (chargerEstimatorData.elevator === undefined) {
      showSnack(t("preferences.elevator_required"), "error");
      return;
    }
    if (chargerEstimatorData.pool === undefined) {
      showSnack(t("preferences.pool_required"), "error");
      return;
    }
    if (chargerEstimatorData.hotTub === undefined) {
      showSnack(t("preferences.hot_tub_required"), "error");
      return;
    }
    navigate("?currentPage=contactInformation");
  };

  return (
    <FormPageLayout
      imageSrc={ElevatorImage}
      imageAlt={t("preferences.image_alt")}
      imagePosition="left"
      title={
        <Typography
          style={{ fontWeight: "bold", fontSize: "2.5em" }}
          variant="h2"
          align="inherit"
        >
          {t("preferences.title")}
        </Typography>
      }
      backAction={{
        label: t("nav.back"),
        color: "primary",
        onClick: () => navigate("?currentPage=powerAccess"),
      }}
      primaryAction={{
        label: t("nav.next"),
        color: "warning",
        onClick: handleNext,
      }}
    >
      <div>
        <Typography
          style={{ marginBottom: theme.spacing(1) }}
          variant="body2"
          align="inherit"
        >
          {t("preferences.purchase")}
        </Typography>

        <AutoComplete
          textAlign="center"
          size="tiny"
          value={purchaseOrLease?.id || null}
          label={t("nav.please_select")}
          options={translateOptions(t, purchaseOptions)}
          onChange={(value) =>
            setChargerEstimatorData({
              ...chargerEstimatorData,
              purchaseType:
                purchaseOptions.find((o) => o.id === value?.id)?.label || "",
            })
          }
        />
      </div>

      <div>
        <Typography
          style={{ marginBottom: theme.spacing(1) }}
          variant="body2"
          align="inherit"
        >
          {t("preferences.elevator")}
        </Typography>

        <AutoComplete
          textAlign="center"
          size="tiny"
          value={hasElevator}
          label={t("nav.please_select")}
          options={translateOptions(t, yesNoOptions)}
          onChange={(value) =>
            setChargerEstimatorData({
              ...chargerEstimatorData,
              elevator: value ? value.id === 1 : undefined,
            })
          }
        />
      </div>

      <div>
        <Typography
          style={{ marginBottom: theme.spacing(1) }}
          variant="body2"
          align="inherit"
        >
          {t("preferences.pool")}
        </Typography>

        <AutoComplete
          textAlign="center"
          size="tiny"
          value={hasPool}
          label={t("nav.please_select")}
          options={translateOptions(t, yesNoOptions)}
          onChange={(value) =>
            setChargerEstimatorData({
              ...chargerEstimatorData,
              pool: value ? value.id === 1 : undefined,
            })
          }
        />
      </div>

      <div>
        <Typography
          style={{ marginBottom: theme.spacing(1) }}
          variant="body2"
          align="inherit"
        >
          {t("preferences.hot_tub")}
        </Typography>

        <AutoComplete
          textAlign="center"
          size="tiny"
          value={hasHotTub}
          label={t("nav.please_select")}
          options={translateOptions(t, yesNoOptions)}
          onChange={(value) =>
            setChargerEstimatorData({
              ...chargerEstimatorData,
              hotTub: value ? value.id === 1 : undefined,
            })
          }
        />
      </div>
    </FormPageLayout>
  );
};

export default Preferences;
